import React, { useEffect } from 'react';
import {
    Alert, AlertTitle, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import audioSettingsStore from '../../Mobx/AudioSettingsStore';
import { formatError } from '../../Mobx/AudioCatalog';

const HEADERS = ['זמן', 'ספק', 'מודל', 'שפה', 'מגדר', 'קובץ', 'תווים', 'קוד', 'הודעה'];

/** "2.11.2026, 20:41:05" - the time of a failure, in the time of the browser */
export const formatFailureTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('he-IL');
};

/**
 * The most recent failures of the automatic creation (GET /api/ai/settings/audio/failures).
 * The server keeps the last 1,000 in a capped collection, so a problem is seen here and not only in the log of the servers.
 */
const AudioFailures = observer(() => {
    const store = audioSettingsStore;
    const { failures, failuresLoading, failuresError } = store;

    useEffect(() => {
        store.loadFailures();
    }, [store]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h6">כשלים אחרונים ביצירה אוטומטית</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => store.loadFailures()}
                    disabled={failuresLoading}
                    startIcon={failuresLoading ? <CircularProgress size={14} /> : null}
                >
                    רענון הכשלים
                </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                כל יצירה אוטומטית שנכשלה (אחרי הניסיונות החוזרים) נרשמת כאן, החדשים ראשונים. נשמרים 1,000 הכשלים האחרונים.
                מילה שהיצירה שלה נכשלה נשארת בלי קובץ אודיו.
            </Typography>

            {failuresError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    <AlertTitle>טעינת הכשלים נכשלה</AlertTitle>
                    <Box dir="auto">{formatError(failuresError)}</Box>
                </Alert>
            )}

            {failures && failures.length === 0 && !failuresError && (
                <Alert severity="success">לא נרשמו כשלים.</Alert>
            )}

            {failures && failures.length > 0 && (
                <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader aria-label="כשלים אחרונים ביצירה אוטומטית">
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((header) => <TableCell key={header} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failures.map((failure, index) => (
                                <TableRow key={failure._id || index}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}><bdi>{formatFailureTime(failure.createdAt)}</bdi></TableCell>
                                    <TableCell><bdi>{failure.provider}</bdi></TableCell>
                                    <TableCell><bdi>{failure.model}</bdi></TableCell>
                                    <TableCell><bdi>{failure.language}</bdi></TableCell>
                                    <TableCell><bdi>{failure.gender}</bdi></TableCell>
                                    <TableCell><bdi>{failure.fileName}</bdi></TableCell>
                                    <TableCell>{failure.chars}</TableCell>
                                    <TableCell>{failure.statusCode || ''}</TableCell>
                                    <TableCell sx={{ minWidth: 240 }}><Box dir="auto">{failure.message}</Box></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
});

export default AudioFailures;
